import { useState } from "react";
import { BookOpen, Check, Copy, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "../components/Reveal";
import { CLAUSE_TEXT } from "../data/content";

export const Clause = () => {
    const [copied, setCopied] = useState(false);

    const copyClause = async () => {
        try {
            await navigator.clipboard.writeText(CLAUSE_TEXT);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = CLAUSE_TEXT;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
        }
        setCopied(true);
        toast.success("Cláusula copiada para a área de transferência.");
        setTimeout(() => setCopied(false), 2600);
    };

    return (
        <section id="clausula" data-testid="clause-section" className="bg-cream-dark py-24 sm:py-32">
            <div className="mx-auto max-w-5xl px-5 sm:px-8">
                <SectionHead
                    eyebrow="Esboço contratual"
                    title="Cláusula compromissória modelo"
                    lead="Um esboço de cláusula arbitral para inserção em contratos empresariais, submetendo futuras controvérsias à administração da CAJUSP."
                />

                <Reveal delay={0.12}>
                    <div className="mt-14 border border-cream-line bg-white">
                        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-cream-line px-6 py-4 sm:px-8">
                            <p className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.24em] text-gold-dark">
                                <FileText size={14} />
                                Esboço — sujeito a adequação
                            </p>
                            <button
                                type="button"
                                data-testid="clause-copy-button"
                                onClick={copyClause}
                                className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                                    copied
                                        ? "border-gold bg-gold text-graphite-deep"
                                        : "border-cream-line text-graphite-text hover:border-gold hover:text-gold-dark"
                                }`}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                {copied ? "Copiada" : "Copiar cláusula"}
                            </button>
                        </div>
                        <div className="px-6 py-8 sm:px-10 sm:py-10">
                            <p
                                data-testid="clause-text"
                                className="font-serif text-lg leading-loose text-ink sm:text-xl"
                            >
                                “Todas as controvérsias decorrentes ou relacionadas ao presente
                                contrato, incluindo as relativas à sua existência, validade,
                                eficácia, interpretação, execução, violação ou rescisão, serão
                                definitivamente solucionadas por arbitragem, administrada pela{" "}
                                <strong className="font-semibold">
                                    CAJUSP — Câmara de Juízo Arbitral e Mediação Empresarial de São
                                    Paulo
                                </strong>
                                , de acordo com o seu Regulamento de Arbitragem. O Tribunal Arbitral
                                será composto por{" "}
                                <span className="bg-gold/15 px-1">
                                    [um árbitro / três árbitros]
                                </span>
                                , nomeados conforme o referido Regulamento. A sede da arbitragem
                                será a cidade de São Paulo, Estado de São Paulo; o idioma será o
                                português; e a lei aplicável ao mérito será a legislação brasileira.
                                A sentença arbitral será definitiva e obrigatória para as partes e
                                seus sucessores, nos termos da Lei nº 9.307/1996.”
                            </p>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <p className="mt-6 text-xs leading-relaxed text-graphite-text">
                        Texto meramente indicativo. A redação definitiva da cláusula compromissória
                        deve ser adequada ao caso concreto com assessoria jurídica especializada.
                    </p>
                </Reveal>

                <Reveal delay={0.26}>
                    <div className="mt-10 flex flex-wrap items-center justify-between gap-5 border border-cream-line bg-white p-6 sm:px-8">
                        <div className="flex items-center gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-gold/10 text-gold-dark">
                                <BookOpen size={20} strokeWidth={1.8} />
                            </span>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-graphite-text">
                                    Documento institucional · esboço
                                </p>
                                <p className="mt-0.5 font-serif text-xl text-ink">
                                    Regulamento de Arbitragem da CAJUSP
                                </p>
                            </div>
                        </div>
                        <a
                            href="/regulamento-cajusp-esboco.pdf"
                            download
                            data-testid="regulamento-download-button"
                            className="inline-flex items-center gap-2.5 rounded-sm bg-gold px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-graphite-deep transition-colors hover:bg-gold-light"
                        >
                            <Download size={14} strokeWidth={2.2} />
                            Baixar PDF
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
